import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import {
  email,
  form,
  FormField,
  FormOptions,
  FormRoot,
  required,
  SchemaPathTree,
  TreeValidationResult,
  validate,
} from '@angular/forms/signals';
import { Tab, TabContent, TabList, TabPanel, Tabs } from '@angular/aria/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { FormFieldOnErrorDirective } from 'src/app/shared/directives/form-field-on-error/form-field-on-error.directive';
import { OTPChannelType } from 'src/app/shared/model/auth-channels';
import { PortalType } from 'src/app/shared/model/IWebConfig';
import { SignalFormService } from 'src/app/shared/services/signal-form/signal-form.service';
import { PhoneNumberInputComponent } from '../../../components/phone-number-input/phone-number-input.component';
import { ForgotPasswordService } from '../forgot-password.service';
import { CredentialsStepFormModel } from './credentials-step.model';

export interface CredentialsSubmission {
  username: string;
  channelType: OTPChannelType;
}

@Component({
  selector: 'app-forgot-password-credentials-step',
  templateUrl: './credentials-step.component.html',
  styleUrls: ['./credentials-step.component.scss'],
  imports: [
    TranslatePipe,
    PhoneNumberInputComponent,
    FormField,
    FormRoot,
    FormFieldOnErrorDirective,
    Tab,
    TabContent,
    TabList,
    TabPanel,
    Tabs,
  ],
  providers: [ForgotPasswordService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class CredentialsStepComponent {
  #forgotPasswordService = inject(ForgotPasswordService);

  public portal = input<PortalType>();
  public loginMethod = input<string[]>([]);
  public send = output<CredentialsSubmission>();

  public active = '1';
  public separateDialCode = false;
  public preferredCountries: string[] = ['za', 'gb', 'us'];

  formModel = signal<CredentialsStepFormModel>({ email: '', mobile: null });
  formOptions: FormOptions<CredentialsStepFormModel> = SignalFormService.formSubmitOptions(this.sendOtp.bind(this));
  form = form(this.formModel, this.#formValidation.bind(this), this.formOptions);

  public async sendOtp(): Promise<TreeValidationResult> {
    const model = this.formModel();
    const email = model.email?.toLowerCase().trim();
    const mobile = model.mobile?.e164Number;

    const username = this.#forgotPasswordService.resolveUsername(email, mobile);
    const channelType = this.#forgotPasswordService.resolveChannelType(email, mobile);

    this.send.emit({ username, channelType });
    return undefined;
  }

  #formValidation(path: SchemaPathTree<CredentialsStepFormModel>): void {
    email(path.email);
    required(path.email, { when: () => this.#isSingleMethod('email') });
    required(path.mobile, { when: () => this.#isSingleMethod('mobile') });

    validate(path.mobile, ({ value, valueOf }) => {
      if (!this.#isBothMethods()) return null;
      const hasEmail = !!valueOf(path.email);
      const hasMobile = !!value()?.e164Number;
      return hasEmail === hasMobile ? { kind: 'exclusiveCredential' } : null;
    });
  }

  #isBothMethods(): boolean {
    return this.loginMethod().includes('email') && this.loginMethod().includes('mobile');
  }

  #isSingleMethod(method: 'email' | 'mobile'): boolean {
    return !this.#isBothMethods() && this.loginMethod().includes(method);
  }
}
